import React, { useMemo } from "react";
import ApiService from "@/api/ApiService";
import DeviceMenu from "./DeviceMenu";
import { useQuery } from "@tanstack/react-query";
import { Box, CircularProgress, Typography } from "@mui/material";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { usePersistentDataGrid } from "@/shared/hooks/usePersistentDataGrid";
import dayjs from "dayjs";

interface FuelingEvent {
  id: number;
  meterReading: {
    runningHours: number;
  } | null;
  liters: string; // decimal like "250.00"
  comment: string;
  createdAt: string;
  createdBy: { id: number; name: string; firstName: string; phone: string; email: string };
}

type FuelingRow = {
  id: number;
  createdAt: string;
  liters: number | null;
  creator: string;
  runningHours: number | null;
  comment: string;
};

function getColumns(): GridColDef<FuelingRow>[] {
  return [
    {
      field: "createdAt",
      headerName: "Datum",
      type: "dateTime",
      flex: 1,
      valueGetter: (_value, row) => (row.createdAt ? new Date(row.createdAt) : null),
      valueFormatter: (value) => {
        if (!value) return "";
        return dayjs(value as Date).format("DD.MM.YYYY HH:mm [Uhr]");
      },
    },
    {
      field: "liters",
      headerName: "Liter",
      type: "number",
      flex: 0.6,
      align: "right",
      headerAlign: "right",
      valueGetter: (_value, row) => row.liters,
      valueFormatter: (value) => {
        if (value === null || value === undefined) return "";
        return `${Number(value).toLocaleString("de-DE", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })} l`;
      },
    },
    {
      field: "creator",
      headerName: "Ersteller",
      type: "string",
      flex: 1,
      valueGetter: (_value, row) => row.creator,
    },
    {
      field: "runningHours",
      headerName: "Betriebsstunden",
      type: "number",
      flex: 0.8,
      align: "right",
      headerAlign: "right",
      valueGetter: (_value, row) => row.runningHours,
      valueFormatter: (value) => {
        if (value === null || value === undefined) return "";
        return `${Number(value).toLocaleString("de-DE", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })} h`;
      },
    },
    {
      field: "comment",
      headerName: "Kommentar",
      type: "string",
      flex: 1.5,
      valueGetter: (_value, row) => row.comment ?? "",
    },
  ];
}

export default function DeviceFuel({ setVisiblePage, deviceId, image }: { setVisiblePage: (t: string) => void; deviceId: number }) {
  const query = useQuery<FuelingEvent[], any, FuelingEvent[]>({
    queryKey: ["getFuelingEvents", deviceId],
    queryFn: ApiService.getEntitiesWithFilter("fuelingEvent", { resource: ["resource", deviceId ?? 0] }, "stoffstrom"),
    enabled: (deviceId ?? 0) > 0,
  });

  const rows = useMemo<FuelingRow[]>(() => {
    return (query.data ?? []).map((event) => ({
      id: event.id,
      createdAt: event.createdAt,
      liters: event.liters ? Number(event.liters) : null,
      creator: `${event.createdBy?.firstName ?? ""} ${event.createdBy?.name ?? ""}`.trim(),
      runningHours: event.meterReading?.runningHours ?? null,
      comment: event.comment ?? "",
    }));
  }, [query.data]);

  const { gridProps } = usePersistentDataGrid("device-fueling-events", getColumns(), {
    sortModel: [{ field: "createdAt", sort: "desc" }],
  });

  const isEmpty = query.isSuccess && query.data.length === 0;

  return (
    <>
      <DeviceMenu type="FUEL" image={image} setType={setVisiblePage} />

      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", border: "1px solid #002169", borderTop: "none", display: "flex" }}>
        {query.isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4, alignSelf: "center", color: "#444", flex: 1 }}>
            <CircularProgress size={24} />
          </Box>
        ) : query.isError ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4, alignSelf: "center", color: "#444", flex: 1 }}>
            <Typography color="error">Fehler beim Laden der Tankungen.</Typography>
          </Box>
        ) : isEmpty ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4, alignSelf: "center", color: "#444", flex: 1 }}>
            <Typography>Nichts gefunden</Typography>
          </Box>
        ) : (
          <DataGridPro<FuelingRow>
            rows={rows}
            rowHeight={36}
            getRowId={(row) => row.id}
            loading={query.isLoading || query.isFetching}
            showToolbar
            {...gridProps}
          />
        )}
      </Box>
    </>
  );
}
